import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ContentContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      
      // Transform array to nested object for easy access
      const contentMap = {};
      response.data.forEach(item => {
        if (!contentMap[item.page]) {
          contentMap[item.page] = {};
        }
        if (!contentMap[item.page][item.section]) {
          contentMap[item.page][item.section] = {};
        }
        contentMap[item.page][item.section][item.key] = {
          id: item.id,
          nl: item.content_nl,
          en: item.content_en,
          fr: item.content_fr,
          de: item.content_de,
          type: item.content_type
        };
      });
      
      setContent(contentMap);
      setError(null);
    } catch (err) {
      console.error('Error fetching content:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Get content by page.section.key with language
  const getContent = useCallback((path, language = 'nl') => {
    const parts = path.split('.');
    let value = content;
    
    for (const part of parts) {
      if (value && value[part]) {
        value = value[part];
      } else {
        return null;
      }
    }
    
    // If we got an object with language keys, return the right language
    if (value && typeof value === 'object') {
      return value[language] || value.nl || null;
    }
    
    return value;
  }, [content]);

  // Get raw content item (for admin editing)
  const getContentItem = useCallback((path) => {
    const parts = path.split('.');
    let value = content;
    
    for (const part of parts) {
      if (value && value[part]) {
        value = value[part];
      } else {
        return null;
      }
    }
    
    return value;
  }, [content]);

  // Check if content is loaded
  const hasContent = useCallback((path) => {
    return getContent(path) !== null;
  }, [getContent]);

  return (
    <ContentContext.Provider value={{ 
      content, 
      loading, 
      error, 
      getContent, 
      getContentItem,
      hasContent,
      refreshContent: fetchContent 
    }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export default ContentContext;
